/*
 * Featured comparison table — decorative parallax
 * Translates each decoration vertically based on the section's scroll progress
 * through the viewport. Active on desktop only (>= 990px) and disabled when the
 * user prefers reduced motion.
 */
class ComparisonParallax extends HTMLElement {
  constructor() {
    super();
    this.items = Array.from(this.querySelectorAll('[data-parallax-speed]'));
    this.section = this.closest('.comparison-table');
    this.ticking = false;
    this.mql = window.matchMedia('(min-width: 990px)');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.evaluate = this.evaluate.bind(this);
  }

  connectedCallback() {
    if (!this.items.length || !this.section) return;
    this.evaluate();
    this.mql.addEventListener('change', this.evaluate);
    this.reduceMotion.addEventListener('change', this.evaluate);
  }

  disconnectedCallback() {
    this.stop();
    this.mql.removeEventListener('change', this.evaluate);
    this.reduceMotion.removeEventListener('change', this.evaluate);
  }

  evaluate() {
    if (this.mql.matches && !this.reduceMotion.matches) {
      this.start();
    } else {
      this.stop();
    }
  }

  start() {
    if (this.active) return;
    this.active = true;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  stop() {
    this.active = false;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.items.forEach((item) => {
      item.style.transform = 'translate(-50%, -50%)';
    });
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    if (!this.active) return;

    const rect = this.section.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    // -1 when the section is below the viewport, +1 when above; 0 when centred.
    const denom = viewportH / 2 + rect.height / 2;
    const progress = denom === 0 ? 0 : (viewportH / 2 - (rect.top + rect.height / 2)) / denom;

    this.items.forEach((item) => {
      const speed = parseFloat(item.dataset.parallaxSpeed) || 0;
      const offset = -progress * speed * 12;
      item.style.transform = `translate(-50%, calc(-50% + ${offset.toFixed(1)}px))`;
    });
  }
}

customElements.define('comparison-parallax', ComparisonParallax);
